import { FC, useState } from 'react';
import { Modal, Typography, Table, Badge, Input } from '@crisellevaldez/speculo-ui';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { BackButton } from '../../common/BackButton';
import {
  User,
  Voicemail,
  XCircle,
  PhoneIncoming,
  PhoneOutgoing,
  Phone,
  Search,
} from 'lucide-react';
import { ReactNode } from 'react';
import { CallDetailsPanel } from '../contacts/CallDetailsPanel';
import { EditContactModal } from '../contacts/EditContactModal';
import { EditCallAnalysisModal } from '../contacts/EditCallAnalysisModal';
import { formatDate } from '../../../utils/dateTime';
import { formatNumber } from '../../../utils/format';
import { useDebounce } from '../../../hooks/useDebounce';
import { formatPhoneNumber } from '../../../utils/formatters';
import { formatDurationFromSeconds } from '../../../utils/duration';
import { getAnsweredByStyle, getFlowTypeStyle } from '../../../utils/badgeStyles';
import { OutcomeBadge } from '../../common/OutcomeBadge';
import {
  useFlowCallsGQL,
  type GQLCall as Call,
} from '../../../hooks/graphql/flows/useFlowCallsGQL';

interface Column {
  header: ReactNode;
  key: string;
  cell: (item: Call) => ReactNode;
  width?: string;
  sortable?: boolean;
  isPinned?: boolean;
  pinPosition?: string;
}

interface FlowCallRecordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  orgTimezone?: string;
  organization?: {
    id: string;
    [key: string]: unknown;
  };
}

export const FlowCallRecordsModal: FC<FlowCallRecordsModalProps> = ({
  isOpen,
  onClose,
  flowId,
  flowName,
  orgTimezone,
  organization,
}) => {
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [editOutcomeCallId, setEditOutcomeCallId] = useState<string | null>(null);
  const [showInfoOnMobile, setShowInfoOnMobile] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<
    'callTimestamp' | 'corrected_duration' | 'answered_by'
  >('callTimestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const { calls, isLoading, total, currentPage, pageSize, nextPage, prevPage, goToPage } =
    useFlowCallsGQL({
      isOpen,
      flowId,
      organizationId: organization?.id || '',
      sortField,
      sortDirection,
      search: debouncedSearch,
    });

  const handleSort = (key: string, direction: 'asc' | 'desc') => {
    setSortField(key as 'callTimestamp' | 'corrected_duration' | 'answered_by');
    setSortDirection(direction);
  };

  const columns: Column[] = [
    {
      header: 'Name',
      key: 'fullName',
      width: '200px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item) => (
        <div className="flex items-center gap-3 min-w-0">
          {item.inbound ? (
            <PhoneIncoming className="h-3.5 w-3.5 text-blue-500 shrink-0" />
          ) : (
            <PhoneOutgoing className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          )}
          {item.contactId ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedContactId(String(item.contactId));
              }}
              className="font-medium text-left text-primaryfortext underline truncate min-w-0 cursor-pointer"
              title={item.fullName || ''}
            >
              {item.fullName || '-'}
            </button>
          ) : (
            <span
              className="font-medium text-left text-muted-foreground truncate min-w-0"
              title={item.fullName || ''}
            >
              {item.fullName || '-'}
            </span>
          )}
          {item.isTest && <Badge className="inline-flex items-center badge-amber">Test</Badge>}
        </div>
      ),
    },
    {
      header: 'Phone',
      key: 'to_number',
      width: '160px',
      cell: (item) => <span className="text-foreground">{formatPhoneNumber(item.to_number)}</span>,
    },
    {
      header: 'Date/Time',
      key: 'callTimestamp',
      width: '220px',
      sortable: true,
      cell: (item) => {
        const timestamp = item.callTimestamp;
        return timestamp ? (
          <span className="whitespace-nowrap text-foreground">
            {formatDate(timestamp, orgTimezone, 'MMM D, h:mm A')}
          </span>
        ) : (
          '-'
        );
      },
    },
    {
      header: 'Duration',
      key: 'corrected_duration',
      sortable: true,
      width: '100px',
      cell: (item) => {
        const seconds = item.corrected_duration || item.duration;
        if (seconds != null && typeof seconds === 'number' && seconds > 0) {
          return (
            <span className="text-foreground tabular-nums">
              {formatDurationFromSeconds(seconds)}
            </span>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
    {
      header: 'Answered By',
      key: 'answered_by',
      width: '140px',
      sortable: true,
      cell: (item) => {
        const answeredBy = (item.answered_by || 'no answer') as 'human' | 'voicemail' | 'no answer';
        const style = getAnsweredByStyle(answeredBy);
        if (!style) return null;
        return (
          <Badge className={`inline-flex items-center gap-1.5 ${style.background} ${style.text}`}>
            {answeredBy === 'human' ? (
              <>
                <User className="w-3 h-3" />
                <span>Human</span>
              </>
            ) : answeredBy === 'voicemail' ? (
              <>
                <Voicemail className="w-3 h-3" />
                <span>Voicemail</span>
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3" />
                <span>No Answer</span>
              </>
            )}
          </Badge>
        );
      },
    },
    {
      header: 'Flow',
      key: 'flowName',
      width: '180px',
      cell: (item) => {
        const name = item.flowName || '';
        if (!name) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="truncate text-foreground" title={name}>
            {name}
          </div>
        );
      },
    },
    {
      header: 'Flow Type',
      key: 'flowType',
      width: '120px',
      cell: (item) => {
        const type = item.flowType || '';
        if (!type) return <span className="text-muted-foreground">-</span>;
        const style = getFlowTypeStyle(type);
        return (
          <Badge className={`inline-flex items-center ${style.background} ${style.text}`}>
            {type}
          </Badge>
        );
      },
    },
    {
      header: 'Outcome',
      key: 'call_analysis',
      width: '150px',
      cell: (item) => {
        const outcome = item.call_analysis?.outcome;
        if (!outcome) return <span className="text-muted-foreground">-</span>;
        return <OutcomeBadge outcome={outcome} />;
      },
    },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setSelectedCall(null);
          setShowInfoOnMobile(false);
          onClose();
        }}
        size="full"
        className="mx-0 h-full"
      >
        <Modal.Header>
          <div className="flex flex-col gap-0.5">
            <Typography variant="h2">{flowName}</Typography>
            <div className="text-xs text-muted-foreground">
              {formatNumber(total)} {total === 1 ? 'record' : 'records'}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="flex-1 flex flex-col min-h-0">
          <div className="mb-4 shrink-0">
            <Input
              placeholder="Search by name or phone..."
              startIcon={<Search className="text-muted-foreground" size={16} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-8 text-sm max-w-sm"
            />
          </div>
          <div className="flex flex-1 min-h-0 gap-4 overflow-hidden md:flex-row flex-col">
            <div
              className={`md:w-[65%] w-full flex flex-col min-h-0 ${showInfoOnMobile ? 'hidden md:flex' : ''}`}
            >
              <div className="flex flex-col flex-1 min-h-0 [&_tbody_tr]:cursor-pointer">
                <TableCard
                  className="flex-1 min-h-0"
                  footer={
                    <OffsetPagination
                      currentPage={currentPage}
                      totalItems={total}
                      pageSize={pageSize}
                      onNextPage={nextPage}
                      onPrevPage={prevPage}
                      onPageChange={goToPage}
                      isLoading={isLoading}
                    />
                  }
                >
                  <Table<Call>
                    data={calls}
                    columns={columns}
                    keyExtractor={(item) => item.call_id}
                    loading={isLoading}
                    emptyStateText="No call records available"
                    rowSelectable
                    selectedRowId={selectedCall?.call_id}
                    onRowSelect={(id) => {
                      const c = calls.find((call) => call.call_id === id);
                      if (c) {
                        setSelectedCall(c);
                        setShowInfoOnMobile(true);
                      }
                    }}
                    className="w-full"
                    sortable={true}
                    onSort={handleSort}
                    size="xl"
                  />
                </TableCard>
              </div>
            </div>
            <div
              className={`md:w-[35%] w-full md:h-full min-h-0 max-h-[60vh] md:max-h-none rounded-xl border border-border bg-muted/30 overflow-y-auto scrollbar-custom ${!showInfoOnMobile && !selectedCall ? 'hidden md:block' : ''}`}
            >
              {showInfoOnMobile && (
                <div className="p-4 border-b border-border md:hidden">
                  <BackButton
                    onClick={() => {
                      setShowInfoOnMobile(false);
                      setSelectedCall(null);
                    }}
                  />
                </div>
              )}
              {selectedCall ? (
                <CallDetailsPanel
                  call={selectedCall}
                  orgTimezone={orgTimezone}
                  flowInfo={
                    selectedCall.flowName && selectedCall.flowName !== '-'
                      ? {
                          name: selectedCall.flowName,
                          type: selectedCall.flowType || '-',
                        }
                      : undefined
                  }
                  contactFirstName={undefined}
                  assistantName={organization?.assistant_name as string | undefined}
                  onEditOutcome={() =>
                    setEditOutcomeCallId(selectedCall.call_id || selectedCall.id)
                  }
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Select a call to view details
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click any row in the table to see the full call breakdown
                  </p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {selectedContactId && (
        <EditContactModal
          isOpen={!!selectedContactId}
          onClose={() => setSelectedContactId(null)}
          onSubmit={async () => {}}
          onSuccess={() => {}}
          contact={{ id: selectedContactId }}
        />
      )}

      {editOutcomeCallId && (
        <EditCallAnalysisModal
          isOpen={!!editOutcomeCallId}
          onClose={() => setEditOutcomeCallId(null)}
          callId={editOutcomeCallId}
          currentOutcome={selectedCall?.call_analysis?.outcome || ''}
          onSuccess={(newOutcome) => {
            if (selectedCall) {
              setSelectedCall({
                ...selectedCall,
                call_analysis: { ...selectedCall.call_analysis, outcome: newOutcome },
              } as Call);
            }
          }}
        />
      )}
    </>
  );
};
